/**
 * Viewport queries for the phone and tablet layouts, following changes (rotation, a resized window).
 *
 * - PHONE: Mobile.css's own breakpoint (below 768px): FlowSidebar is an off-canvas drawer.
 * - COMPACT: phones and tablets in portrait (below 1024px): a project shows one pane at a time, Chat or Project.
 */
import { useEffect, useState } from 'react';

export const PHONE_QUERY = '(max-width: 767px)';
export const COMPACT_QUERY = '(max-width: 1023px)';

const matches = (query) => typeof window !== 'undefined' && Boolean(window.matchMedia?.(query)?.matches);

export default function useMediaQuery(query) {
  const [on, setOn] = useState(() => matches(query));
  useEffect(() => {
    const list = typeof window !== 'undefined' ? window.matchMedia?.(query) : null;
    if (!list) { return undefined; }
    const onChange = () => setOn(list.matches);
    onChange();
    list.addEventListener?.('change', onChange);
    return () => list.removeEventListener?.('change', onChange);
  }, [query]);
  return on;
}

export const usePhone = () => useMediaQuery(PHONE_QUERY);
export const useCompact = () => useMediaQuery(COMPACT_QUERY);

/**
 * Whether an element is narrower than `px` (a ResizeObserver): the Canvas beside the chat on a 1024px screen is as
 * narrow as a tablet, so its header, file tree and editor take their compact form there too. `ref` is a React ref.
 */
export function useNarrowElement(ref, px) {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect?.width ?? el.getBoundingClientRect().width;
      if (width > 0) { setNarrow(width < px); }
    });
    observer.observe(el);
    return () => observer.disconnect();
  });
  return narrow;
}

/**
 * An element's width (a ResizeObserver), 0 until it is known (and where ResizeObserver is missing): headers use it
 * to fold their buttons into a "⋯" menu as they get narrow. `ref` is a React ref.
 */
export function useElementWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(([entry]) => {
      const next = entry?.contentRect?.width ?? el.getBoundingClientRect().width;
      if (next > 0) { setWidth(Math.round(next)); }
    });
    observer.observe(el);
    return () => observer.disconnect();
  });
  return width;
}
